import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../hooks/shared';
import {
  getCalendarDateFromStorage,
  saveCalendarDateToStorage,
} from '../../lib/navigationUtils';
import { parseWorkspaceSlugFromUrl } from '../../lib/workspaceUtils';
import { useEventInstanceStore, useNavigationService } from '../../stores';
import { Button } from './Button';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigationService = useNavigationService();
  const { t } = useLanguage();
  const eventInstanceStore = useEventInstanceStore();

  // Get workspace slug from URL
  const workspaceSlug = parseWorkspaceSlugFromUrl();

  // Don't render navigation if workspace slug is not available yet
  if (!workspaceSlug) {
    return null;
  }

  // Enhanced calendar navigation with localStorage persistence
  const handleCalendarClick = () => {
    // Parse date directly from current pathname
    const calendarMatch = location.pathname.match(
      /\/calendar\/(day|week|month)\/(\d{4}-\d{2}-\d{2})/
    );
    const trainingMatch = location.pathname.match(/\/training\/(.+)/);

    let dateFromUrl = null;
    if (calendarMatch) {
      dateFromUrl = calendarMatch[2]; // Extract date from calendar URL
    } else if (trainingMatch) {
      // Get training date from store
      const trainingId = trainingMatch[1];
      const instance = eventInstanceStore.instances.find(
        (inst) => inst.id === trainingId
      );
      if (instance) {
        dateFromUrl = new Date(instance.startTime).toISOString().split('T')[0]; // yyyy-MM-dd
      }
    }

    // If we're already in calendar view (week or month), jump to today's date
    if (
      location.pathname.includes('/calendar/week/') ||
      location.pathname.includes('/calendar/month/')
    ) {
      const today = new Date().toISOString().split('T')[0]; // yyyy-MM-dd
      saveCalendarDateToStorage(workspaceSlug, today);
      if (location.pathname.includes('/calendar/month/')) {
        navigationService.toMonth(workspaceSlug, today);
      } else {
        navigationService.toWeek(workspaceSlug, today);
      }
      return;
    }

    let targetDate: string;

    if (dateFromUrl) {
      // Use the date from URL
      targetDate = dateFromUrl;
    } else {
      // Try to get date from localStorage
      const storedDate = getCalendarDateFromStorage(workspaceSlug);
      if (storedDate) {
        targetDate = storedDate;
      } else {
        // Fallback to today
        targetDate = new Date().toISOString().split('T')[0]; // yyyy-MM-dd
      }
    }

    // Navigate to calendar with the target date
    navigationService.toWeek(workspaceSlug, targetDate);
  };

  const navItems = [
    {
      path: null,
      label: t('navigation.calendar'),
      icon: '📅',
      onClick: handleCalendarClick,
    },
    {
      path: `/w/${workspaceSlug}/team`,
      label: t('navigation.team'),
      icon: '👥',
      onClick: () => navigationService.toTeam(workspaceSlug),
    },
    {
      path: `/w/${workspaceSlug}/billing`,
      label: t('billing.title'),
      icon: '💳',
      onClick: () => navigationService.toBilling(workspaceSlug),
    },
    {
      path: `/w/${workspaceSlug}/settings`,
      label: t('navigation.settings'),
      icon: '⚙️',
      onClick: () => navigationService.toSettings(workspaceSlug),
    },
  ];

  // Check if current path is calendar-related for active state
  const isCalendarActive =
    location.pathname.includes('/calendar') ||
    location.pathname.includes('/training') ||
    location.pathname === `/w/${workspaceSlug}`;

  return (
    <nav className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 px-1 py-1">
        <div className="flex items-center space-x-0.5">
          {navItems.map((item, index) => (
            <div key={item.path || index} className="relative">
              <Button
                variant={
                  (item.path && location.pathname === item.path) ||
                  (!item.path && isCalendarActive)
                    ? 'default'
                    : 'ghost'
                }
                size="sm"
                onClick={item.onClick}
                className={`
                  flex flex-col items-center space-y-1 h-auto py-2 px-3 rounded-xl transition-all duration-300
                  ${
                    (item.path && location.pathname === item.path) ||
                    (!item.path && isCalendarActive)
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'hover:bg-gray-100/50 text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                <span className="text-lg transition-transform duration-300 group-hover:scale-110">
                  {item.icon}
                </span>
                <span className="text-xs font-medium">{item.label}</span>
              </Button>
              {((item.path && location.pathname === item.path) ||
                (!item.path && isCalendarActive)) && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
